import classNames from "classnames";
import { DataDisplayRecord } from "components/data-display";
import { GridLayout } from "components/grid";
import { DocumentViewer } from "components/media/DocumentViewer";
import { Breadcrumbs } from "components/navigation";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { BlobStorageService } from "services";
import { DocumentsService } from "services/DocumentsService";
import { Link } from "react-router-dom";
import { parseBytesTo } from "utils/parseBytesTo";
import { DateToLocal } from "components/dates";
import { NotificationToaster } from 'components/notifications/NotificationToaster';
import { Intent } from "@blueprintjs/core";
import { Button, LinkButton } from "components/buttons";
import moment from "moment";
import { AlertConfirm, NotificationInline } from "components/notifications";
import { ShowHide } from "components/layout";
import { useSelector } from "react-redux";

export function SingleDocument({ breadcrumbs }) {
    const [document, setDocument] = useState({
        id: 0,
        fileName: "loading",
        version: "loading",
        type: {
            id: 0,
            name: "loading",
            area: "loading",
            featureEnabledDocumentTypeKey: "loading"
        },
        uploadedBy: "loading",
        uploadedOn: "loading",
        additionalProperties: {},
        fileInformation: {
            storageName: "loading",
            containerName: "loading",
            fileName: "loading",
            downloadName: "loading",
            token: "loading",
            documentationByteSize: 0
        },
        entities: []
    });

    const [documentDetails, setDocumentDetails] = useState([{
        key: 'filename',
        label: 'File Name',
        value: 'Loading...'
    }]);

    const [loading, setLoading] = useState(true);
    const [downloadingInProgress, setDownloadingInProgress] = useState(false);
    const [invalidDocument, setInvalidDocument] = useState(false);

    const { id } = useParams();

    const [url, setUrl] = useState("");

    const [deleting, isDeleting] = useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);

    const route = useSelector(state => state.route.route);
    const title = useSelector(state => state.route.title);
    const routeId = useSelector(state => state.route.id);
    const fullBreadcrumbs = useMemo(() => {
        var clonedBreadcrumbs = [...breadcrumbs];

        if (routeId != null && route != null) {
            clonedBreadcrumbs.push({ title: title, location: `/${route}/${routeId}/documents` }, { title: "View Document" });
        } else {
            clonedBreadcrumbs.push({ title: "DOCUMENTS", location: `/document-settings/documents` }, { title: "View Document" })
        }
        return clonedBreadcrumbs;
    }, [breadcrumbs, route, routeId, title]);

    const additionalPropertiesMap = [
        {
            key: 'PrintoutForDate',
            label: 'Printout For Date',
            format: (value) => <DateToLocal format="DD/MM/YYYY">{value}</DateToLocal>
        }
    ]

    useEffect(() => {
        if (!loading) {
            const file = document.fileInformation;
            setUrl(`https://${file?.storageName}.blob.core.windows.net/${file.containerName}/${file?.fileName}?${file?.token}`);
        }
    }, [loading, document]);

    useEffect(() => {
        if (id) {
            DocumentsService.getDocument(id).then((data) => {
                setDocument(data);
                setLoading(false);
            }, (error) => {
                const noAccessCode = 403;
    
                if (error.status === noAccessCode) {
                    setInvalidDocument(true);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not load document. Please try again.");
                }
            });
        }
    }, [id]);

    const renderEntityLink = (entity) => {
        switch (entity.entityType) {
            case "Employee": 
                return <a href={`${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${entity.id}`}>{entity.name}</a>;
            case "Asset": 
                return  <Link to={`/asset/${entity.id}/details`}>{entity.name}</Link>;
            default:
                return entity.name;
        }
    }

    useEffect(() => {
        if (!loading) {
            const details = [
            {
                key: 'filename',
                label: 'File Name',
                value: document.fileName,
                priority: 1
            }];

            document.entities.forEach((entity) => {
                details.push({
                    key: entity.entityType,
                    label: entity.entityType,
                    value: renderEntityLink(entity),
                    priority: 2
                });
            });

            const mainEntity = document.entities.find(e => e.isMainEntity);
            if (mainEntity && mainEntity.locationName) {
                details.push({
                    key: 'location',
                    label: 'Location',
                    value: mainEntity.locationName,
                    priority: 2
                });
            }

            details.push({
                key: 'size',
                label: 'Size',
                value: parseBytesTo(document.fileInformation.documentationByteSize).auto(),
                priority: 3
            }, {
                key: 'version',
                label: 'Version',
                value: document.version,
                priority: 4
            }, {
                key: 'uploadedDate',
                label: 'Upload Date',
                value: <DateToLocal format="DD/MM/YYYY">{document.uploadedOn}</DateToLocal>,
                priority: 5
            }, {
                key: 'deleteDate',
                label: 'Delete Date',
                value: document.deletedOn ? <DateToLocal format="DD/MM/YYYY">{document.deletedOn}</DateToLocal> : "N/A",
            });

            if (document.additionalProperties) {
                additionalPropertiesMap.forEach((prop) => {
                    if (document.additionalProperties.hasOwnProperty(prop.key)) {
                        details.push({
                            key: prop.key,
                            label: prop.label,
                            value: prop.format ? prop.format(document.additionalProperties[prop.key]) : document.additionalProperties[prop.key],
                            priority: 6
                        });
                    }
                });
            }

            updateDetails(details);
        }
    }, [document, loading]);

    const canDelete = useMemo(() => {
        return !!document?.type && !document.type.featureEnabledDocumentTypeKey
    }, [document]);

    const updateDetails = (newDetails) => {
        setDocumentDetails((prev) => {
            const updated = [...prev];
            newDetails.forEach((detail) => {
                const index = updated.findIndex(d => d.key === detail.key);
                if (index !== -1) {
                    updated[index] = detail;
                } else {
                    updated.push(detail);
                }
            });

            updated.sort((a, b) => a.priority - b.priority);

            return updated;
        });
    }

    function downloadFile() {
        const onError = () => {
            setDownloadingInProgress(false);
            NotificationToaster.show(Intent.DANGER, "Unable to download file, please try again.", false);
        }
        setDownloadingInProgress(true);
        NotificationToaster.show(Intent.SUCCESS, "Your file will be downloaded shortly", false);


        BlobStorageService.downloadFile(
            "https://" + document.fileInformation.storageName + ".blob.core.windows.net",
            document.fileInformation.containerName,
            document.fileInformation.fileName,
            document.fileInformation.token,
            document.fileInformation.downloadName).then(() => {
                setDownloadingInProgress(false);
            }, onError);
    }

    function deleteDocument() {
        isDeleting(true);
        DocumentsService.deleteDocument(document.id).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Document deleted successfully.");
            isDeleting(false);
            setShowDeleteWarning(false);

            setTimeout(() => {
                window.location.href = fullBreadcrumbs[fullBreadcrumbs.length - 2].location;
            }, 1000);
        }, () => {
            isDeleting(false);
            NotificationToaster.show(Intent.DANGER, "Unable to delete document, please try again.");
        });
    }

    const getEntityId = (entityType) => document.entities.find(e => e.entityType === entityType)?.id;

    const renderUploadLocationButton = () => {
        const uploadLocationText = "Upload Location";

        if (!document.type.featureEnabledDocumentTypeKey) {
            return <></>;
        }

        let link = '';
        let isExternalLink = false;
        if (document.type.featureEnabledDocumentTypeKey === "printouts") {
            link = `${window.env.VISION_URL}/#/Drivers/RedirectToCalendar?driverId=${getEntityId("Employee")}&activityDate=${moment(document.additionalProperties.printoutForDate).format('YYYY-MM-DD')}`;
            isExternalLink = true;
        } else if (document.type.featureEnabledDocumentTypeKey.endsWith('-checks')) {
            link = `/checks/${document.additionalProperties.CheckId}`;
        } else if (document.type.featureEnabledDocumentTypeKey === "training-documents") {
            link = `${window.env.VISION_URL}/#/Drivers/RedirectToProfile?employeeId=${getEntityId('Employee')}&tab=tab-training`;
            isExternalLink = true;
        } else if (document.type.featureEnabledDocumentTypeKey === "policies") {
            link = `/employee/${getEntityId('Employee')}/documents/policies`;
        }

        return link ? <LinkButton text={uploadLocationText} href={link} externalLink={isExternalLink} intent="Primary" /> : <></>;
    }

    return (
        <UserAccess perform={["Documents:View"]}
            yes={() => (
                <>
                    <ShowHide
                        evaluator={invalidDocument}
                        show={(
                            <div className="row">
                                <Breadcrumbs items={fullBreadcrumbs} />
                                <NotificationInline
                                    allowClose={false}
                                    show
                                    text="You do not have permission to view this type"
                                    intent="danger">
                                </NotificationInline>
                                <div>
                                    <LinkButton intent="primary" text="Back to documents" href="/document-settings/documents" id="return-document-listing" />
                                </div>
                            </div>
                        )}
                        hide={(<>
                            <div className="row">

                                <Breadcrumbs items={fullBreadcrumbs} />

                                <h1 className={classNames({ 'bp6-skeleton': loading })}>{document.type.name}</h1>

                                <div className='button-row spacer-bottom'>
                                    <Button
                                        intent="primary"
                                        text={"Download"}
                                        icon="import"
                                        onClick={downloadFile}
                                        disabled={loading || downloadingInProgress}
                                    />
                                    {renderUploadLocationButton()}
                                    <UserAccess
                                        perform={["Documents:Upload"]}
                                        yes={() => (
                                            <ShowHide
                                                evaluator={canDelete}
                                                show={<Button
                                                    intent="danger"
                                                    text={"Delete"}
                                                    icon="trash"
                                                    onClick={() => setShowDeleteWarning(true)}
                                                    disabled={deleting || loading}
                                                />}
                                            />
                                        )}
                                    />
                                </div>
                                <h2>
                                    Document
                                </h2>
                            </div>

                            <div className='row'>
                                <DocumentViewer 
                                    url={url}
                                    limitMediaHeight={true}
                                    iframeHeight="500px"
                                    loading={loading}
                                    downloadFile={downloadFile}
                                    isDownloading={downloadingInProgress} />
                                <h2 className="spacer-top">Details</h2>
                                <GridLayout gap={40} numberOfColumns={4} className={classNames({ "bp6-skeleton": loading })}>
                                    {documentDetails.map((d) => <DataDisplayRecord key={d.key} header={d.label} data={d.value} />)}
                                </GridLayout>
                            </div>
                        </>)}
                    />

                    <AlertConfirm
                        title="Delete Document"
                        isOpen={showDeleteWarning}
                        onConfirm={deleteDocument}
                        onCancel={() => setShowDeleteWarning(false)}
                        confirmButtonIntent="danger"
                        confirmButtonText="Delete">
                        <p>Are you sure you want to delete this document?</p>
                    </AlertConfirm>
                </>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}